import LocationOnIcon from "@mui/icons-material/LocationOn";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper, { PaperProps } from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useLoadScript } from "@react-google-maps/api";
import { Libraries } from "@react-google-maps/api/dist/utils/make-load-script-url";
import Address from "_shared/addresses/address";
import _ from "lodash";
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";

interface PlaceAutocompleteProps {
  className?: string;
  label: string;
  value: Address | null;
  variant?: "outlined" | "standard";
  requireValidation?: boolean;
  locationServicesDisabledRef?: React.RefObject<boolean>;
  onLocationServicesDisabledChange?: (disabled: boolean) => void;
  onSelected: (value: Address | null) => void;
}

export default function PlaceAutocomplete(props: Readonly<PlaceAutocompleteProps>) {
  const [inputValue, setInputValue] = useState(props.value?.toString());
  const [options, setOptions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [value, setValue] = useState<google.maps.places.AutocompleteSuggestion | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const locationServicesDisabledRef = props.locationServicesDisabledRef;
  const onLocationServicesDisabledChange = props.onLocationServicesDisabledChange;

  // Backoff configuration
  const backoffRef = useRef({
    attempts: 0,
    maxAttempts: 5,
    baseDelay: 300,
  });

  const libraries = useRef<Libraries>(["places"]);
  const { isLoaded: placesApiLoaded, loadError: placesApiLoadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY,
    libraries: libraries.current,
  });

  // Keep the request object in a ref so it persists between renders
  const requestRef = useRef<google.maps.places.AutocompleteRequest>({
    input: "",
    language: "en-US",
  });

  // Use a ref to track the latest request ID to avoid race conditions
  const newestRequestId = useRef(0);

  // Once the API is loaded, refresh the token.
  useEffect(() => {
    if (placesApiLoaded && globalThis.google) {
      refreshToken();
    }
  }, [placesApiLoaded]);

  // Update inputValue when props.value changes
  useEffect(() => {
    if (props.value) {
      setInputValue(props.value.toString());
    } else {
      setInputValue("");
    }
  }, [props.value]);

  // Refresh the session token
  const refreshToken = () => {
    requestRef.current.sessionToken = new globalThis.google.maps.places.AutocompleteSessionToken();
  };

  const isValidCoordinate = (value: number | undefined): boolean => {
    return !Number.isNaN(value) && Number.isFinite(value);
  };

  // Highlight matching text
  const highlightMatches = (text: string | undefined, query: string) => {
    if (!text || !query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? <strong key={btoa(part) + "-" + index}>{part}</strong> : part,
      );
  };

  // Helper function to safely extract address components
  const getAddressComponent = (
    components: google.maps.places.AddressComponent[] | undefined,
    type: string,
  ): string | undefined => {
    if (!components) return;
    const component = components.find((comp) => comp.types.includes(type));
    return component?.shortText ?? undefined;
  };

  // Handle OVER_QUERY_LIMIT with exponential backoff
  const handleOverQueryLimit = (value: string) => {
    console.warn("Google Places API rate limit exceeded");
    setErrorMessage("Too many requests. Please try again in a moment.");

    if (backoffRef.current.attempts < backoffRef.current.maxAttempts) {
      backoffRef.current.attempts++;
      const delay = backoffRef.current.baseDelay * Math.pow(2, backoffRef.current.attempts);
      console.debug(`Retrying in ${delay}ms (attempt ${backoffRef.current.attempts})`);
      setTimeout(() => fetchSuggestions(value), delay);
    } else {
      console.error("Maximum retry attempts reached");
      backoffRef.current.attempts = 0;
    }
  };

  const fetchSuggestions = useCallback(async (value: string) => {
    if (locationServicesDisabledRef?.current) return;
    if (value === "") {
      setOptions([]);
      return;
    }

    // Update the request with the current input
    requestRef.current.input = value;
    const currentRequestId = ++newestRequestId.current;

    setIsLoading(true);
    try {
      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        requestRef.current,
      );

      // If a newer request has been made, ignore this response
      if (currentRequestId !== newestRequestId.current) return;

      setOptions(suggestions);
      setErrorMessage(null);
    } catch (error: unknown) {
      // If a newer request has been made, ignore this error
      if (currentRequestId !== newestRequestId.current) return;
      if ((error as { code: number }).code === 7) {
        // Unregistered user
        onLocationServicesDisabledChange?.(true);
        setErrorMessage("Location service is unavailable.");
        return;
      }

      console.error("Error fetching suggestions:", error);

      const { MapsRequestError, MapsServerError, MapsNetworkError } = (await google.maps.importLibrary(
        "core",
      )) as google.maps.CoreLibrary;

      if (error instanceof MapsRequestError) {
        if (error.code === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
          handleOverQueryLimit(value);
        } else {
          console.error("Google Places API request denied - check API key configuration");
          setErrorMessage("Location service is temporarily unavailable");
        }
      } else if (error instanceof MapsServerError || error instanceof MapsNetworkError) {
        console.error("Google Places API server or network error");
        setErrorMessage("Location service is temporarily unavailable");
      }

      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debounceFetchSuggestions = useRef(_.debounce((value: string) => fetchSuggestions(value), 300)).current;

  // Handle input changes from the Autocomplete component
  const handleInputChange = (_event: SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === "input") {
      if (!newInputValue || newInputValue === "") {
        setOptions([]);
        setValue(null);
        props.onSelected(null);
      }
      setInputValue(newInputValue);
      if (locationServicesDisabledRef?.current) {
        props.onSelected(newInputValue ? new Address({ address1: newInputValue }) : null);
        return;
      }
      debounceFetchSuggestions(newInputValue);
    }
  };

  // When a suggestion is selected, fetch additional place fields
  const onPlaceSelected = async (suggestion: google.maps.places.AutocompleteSuggestion | null) => {
    if (!suggestion) return;

    const placePrediction = suggestion.placePrediction;
    const place = placePrediction?.toPlace();

    if (!place) {
      console.error("Failed to convert place prediction to place");
      return;
    }

    await place.fetchFields({
      fields: ["addressComponents", "location"],
    });

    const streetNumber = getAddressComponent(place.addressComponents, "street_number");
    const route = getAddressComponent(place.addressComponents, "route");

    // Format street address properly handling empty components
    const streetAddress = [streetNumber, route].filter(Boolean).join(" ") || "";

    const lat = place.location?.lat();
    const lng = place.location?.lng();

    if (!isValidCoordinate(lat) || !isValidCoordinate(lng)) {
      console.warn("Invalid coordinates received from Google Places API", { lat, lng });
    }

    const newAddress = {
      address1: streetAddress,
      coordinates: {
        latitude: isValidCoordinate(lat) ? String(lat) : "0",
        longitude: isValidCoordinate(lng) ? String(lng) : "0",
      },
      city: "",
      state: "",
      country: "",
      postalCode: "",
    };

    for (const c of place.addressComponents ?? []) {
      if (c.types.includes("locality")) newAddress.city = c.shortText ?? "";
      if (c.types.includes("administrative_area_level_1")) newAddress.state = c.shortText ?? "";
      if (c.types.includes("country")) newAddress.country = c.shortText ?? "";
      if (c.types.includes("postal_code")) newAddress.postalCode = c.shortText ?? "";
    }

    props.onSelected(new Address({ ...newAddress }));

    setOptions([]);
    setInputValue(suggestion.placePrediction?.text.text);
    refreshToken();
  };

  // Handle selection change from the Autocomplete component
  const handleChange = async (_event: SyntheticEvent, newValue: google.maps.places.AutocompleteSuggestion | null) => {
    setValue(newValue);
    setOptions(newValue ? [newValue, ...options] : options);
    await onPlaceSelected(newValue);
  };

  // Convert a suggestion to a label string
  const getOptionLabel = (option: google.maps.places.AutocompleteSuggestion) => {
    return option.placePrediction?.text.text ?? "Unknown Location";
  };

  const renderPlacesApiLoadError = () => {
    return (
      <TextField
        className={props.className}
        fullWidth
        label={props.label}
        variant={props.variant}
        onChange={(event) => handleInputChange({} as SyntheticEvent, event.target.value, "input")}
        disabled={props.requireValidation}
        error
        helperText="Location service is temporarily unavailable"
      />
    );
  };

  if (placesApiLoadError || locationServicesDisabledRef?.current) return renderPlacesApiLoadError();
  if (!placesApiLoaded && !locationServicesDisabledRef?.current && !placesApiLoadError)
    return (
      <TextField
        className={props.className}
        fullWidth
        label={props.label}
        variant={props.variant}
        disabled
        error={Boolean(errorMessage)}
        helperText={errorMessage}
        slotProps={{
          input: {
            endAdornment: <CircularProgress color="inherit" disableShrink size={20} />,
          },
        }}
      />
    );

  return (
    <Autocomplete
      getOptionLabel={getOptionLabel}
      filterOptions={(x) => x}
      slots={{
        paper: PlaceResultsPaper,
      }}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      loading={isLoading}
      inputValue={inputValue}
      noOptionsText="No locations"
      onChange={handleChange}
      clearOnBlur={true}
      clearOnEscape={true}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          className={props.className}
          fullWidth
          label={props.label}
          variant={props.variant}
          slotProps={{
            inputLabel: {
              shrink: Boolean(inputValue || value),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const name = option.placePrediction?.text.text;
        const description = option.placePrediction?.secondaryText?.text;

        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                <Typography variant="body1" noWrap>
                  {highlightMatches(name, inputValue ?? "")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {description}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}

function PlaceResultsPaper(props: PaperProps) {
  const theme = useTheme();

  return (
    <Paper {...props}>
      {props.children}
      {/* Legal requirement https://developers.google.com/maps/documentation/javascript/policies#logo */}
      <Box
        sx={(staticTheme) => ({
          display: "flex",
          justifyContent: "flex-end",
          p: 1,
          pt: "1px",
          ...staticTheme.applyStyles("dark", {
            opacity: 0.8,
          }),
        })}
      >
        <img
          src={
            theme.palette.mode === "dark"
              ? "https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-non-white3_hdpi.png"
              : "https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
          }
          alt="Powered by Google"
          width="120"
          height="14"
        />
      </Box>
    </Paper>
  );
}
